import type {
  AuthenticatedUser,
  AuthenticatedUserDto,
} from '@src/features/users';
import { clientAccessFromDto } from '@src/features/clients/lib/access';
import { jobTitleFromDto } from '@src/features/users/lib/jobtitle';
import { shortUserFromDto } from '@src/features/users/lib/user';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import AuthenticationContext from './AuthenticationContext';
import type { AuthData, JwtProviderParams } from './providers';
import { JwtAuthProvider } from './providers';
import { useJwtStorage } from './storages';

function createAuthProvider(params: JwtProviderParams) {
  return new JwtAuthProvider(params);
}

export default function AuthenticationProvider({
  children,
}: PropsWithChildren<object>) {
  const storage = useJwtStorage();
  const providerRef = useRef<JwtAuthProvider | null>(null);
  if (providerRef.current === null) {
    providerRef.current = createAuthProvider({ storage });
  }
  const provider = providerRef.current;

  const login = useCallback(
    (username: string, password: string) => provider.login(username, password),
    [provider],
  );
  const verifyTwoFactor = useCallback(
    (challengeToken: string | null, otpCode: string) =>
      provider.verifyTwoFactor(challengeToken, otpCode),
    [provider],
  );
  const startTwoFactorEnrollment = useCallback(
    (token: string) => provider.startTwoFactorEnrollment(token),
    [provider],
  );

  const logout = useCallback(() => provider.logout(), [provider]);
  const verifyToken = useCallback(() => provider.verifyToken(), [provider]);
  const refreshToken = useCallback(() => provider.refreshToken(), [provider]);
  const exchangeImpersonationCode = useCallback(
    (code: string) => provider.exchangeImpersonationCode(code),
    [provider],
  );
  const authenticate = useCallback(
    (data: AuthData) => provider.authenticate(data),
    [provider],
  );
  const replaceTokens = useCallback(
    (tokens: Parameters<typeof provider.replaceTokens>[0]) =>
      provider.replaceTokens(tokens),
    [provider],
  );
  const updateUser = useCallback(
    (user: Partial<AuthenticatedUserDto>) => provider.updateUser(user),
    [provider],
  );
  const unauthenticate = useCallback(
    () => provider.unauthenticate(),
    [provider],
  );

  const contextValue = useMemo(
    () => ({
      login,
      logout,
      verifyToken,
      refreshToken,
      exchangeImpersonationCode,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      authenticate,
      replaceTokens,
      updateUser,
      unauthenticate,
      credentials: storage.credentials,
      isAuthenticated: storage.isAuthenticated,
      user: authenticatedUserFromDto(storage.credentials?.user),
    }),
    [
      authenticate,
      replaceTokens,
      login,
      logout,
      refreshToken,
      exchangeImpersonationCode,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      storage.credentials,
      storage.isAuthenticated,
      updateUser,
      unauthenticate,
      verifyToken,
    ],
  );

  return (
    <AuthenticationContext value={contextValue}>
      {children}
    </AuthenticationContext>
  );
}
AuthenticationProvider.displayName = 'AuthenticationProvider';

function authenticatedUserFromDto(
  user?: AuthenticatedUserDto | null,
): AuthenticatedUser | null {
  if (user) {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      firstName: user.user_first_name,
      familyName: user.user_family_name,
      initials: user.user_initials,
      language: user.language,
      manager: user.user_user_manager
        ? shortUserFromDto(user.user_user_manager)
        : null,
      hrId: user.user_user_hr,
      portrait: user.user_portrait,
      jobTitle: user.user_joti
        ? jobTitleFromDto(user.user_joti)
        : null,
      companyCurrency: user.company_currency ?? 'CHF',
      accessRoles: user.access_roles ?? [],
      permissions: user.permissions ?? [],
      clientAccess: clientAccessFromDto(user.client_access),
    };
  }
  return null;
}
